import { morphSection } from '@theme/section-renderer';
import { CartAddEvent } from '@theme/events';

/**
 * Free-gift size picker (snippets/cart-gift-selector-select.liquid).
 *
 * The gift card's options are native <select>s, so phones use their own
 * picker. Once every option on a card has a value, the matching variant is
 * written to the card's hidden `variant-id` input and, unless the card has
 * its own Add button, the gift is added straight away.
 *
 * Delegated from the document so it keeps working after the cart drawer
 * re-renders, and on the cart page.
 */

const ERROR_MESSAGE = "Sorry, we couldn't add your gift. Please try again.";
const SOLD_OUT_MESSAGE = 'Sorry, that one is sold out. Please choose another.';

/** @param {HTMLElement} card */
function showError(card, message) {
  const error = card.querySelector('.cart-gift-selector__error');
  if (!error) return;
  error.textContent = message;
  error.hidden = !message;
}

/** @param {HTMLElement} card */
function setBusy(card, busy) {
  card.toggleAttribute('data-busy', busy);
  card.querySelectorAll('select[data-gift-option]').forEach((select) => {
    select.disabled = busy;
  });
}

/** @param {HTMLElement} card */
async function addGift(card) {
  if (card.hasAttribute('data-busy')) return;

  const variantId = parseInt(card.querySelector('input[name="variant-id"]')?.value ?? '0', 10);
  if (!variantId) return;

  const sectionId = card.closest('cart-gift-selector-component')?.dataset.sectionId;

  setBusy(card, true);
  showError(card, '');

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _free_gift: 'true' },
        sections: sectionId ? [sectionId] : [],
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status) {
      console.error('[cart-gift-select] add failed:', response.status, data);
      setBusy(card, false);
      showError(card, data.description || data.message || ERROR_MESSAGE);
      return;
    }

    document.dispatchEvent(
      new CartAddEvent(data, String(variantId), {
        source: 'cart-gift-selector-component',
        sections: data.sections ?? {},
      })
    );

    if (sectionId && data.sections?.[sectionId]) {
      morphSection(sectionId, data.sections[sectionId], 'hydration');
    } else {
      setBusy(card, false);
    }
  } catch (error) {
    console.error('[cart-gift-select] add failed:', error);
    setBusy(card, false);
    showError(card, ERROR_MESSAGE);
  }
}

/** @param {Event} event */
function onChange(event) {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('select[data-gift-option]')) return;

  const card = select.closest('.cart-gift-selector__card');
  if (!(card instanceof HTMLElement)) return;

  const selected = [...card.querySelectorAll('select[data-gift-option]')].map((s) => s.value);
  if (selected.some((value) => !value)) return;

  /** @type {Array<{id: number, option1: string|null, option2: string|null, option3: string|null, available: boolean}>} */
  let variants = [];
  try {
    variants = JSON.parse(card.dataset.variants ?? '[]');
  } catch {
    variants = [];
  }

  const variant = variants.find(
    (v) =>
      v.option1 === selected[0] &&
      (selected[1] === undefined || v.option2 === selected[1]) &&
      (selected[2] === undefined || v.option3 === selected[2])
  );

  if (!variant) {
    showError(card, ERROR_MESSAGE);
    return;
  }

  // Sold-out values are already disabled in the list; this catches a sold-out
  // combination of two options (e.g. a size that's only out in one colour).
  if (variant.available === false) {
    showError(card, SOLD_OUT_MESSAGE);
    return;
  }

  showError(card, '');

  const variantInput = card.querySelector('input[name="variant-id"]');
  if (variantInput) variantInput.value = String(variant.id);

  if (!card.querySelector('.cart-gift-selector__add-btn')) addGift(card);
}

/*
 * Several gifts on offer: the cards sit in a horizontal scroll-snap list with
 * prev/next arrows and dots. The compiled cart-gift-selector bundle predates
 * those controls, so they're driven from here. Arrows wrap around (next on
 * the last gift goes back to the first). Listening in the capture phase and
 * stopping the click means a future rebuilt bundle can't double-handle it.
 */

/** @param {HTMLElement} list */
function giftStep(list) {
  const items = list.querySelectorAll('.cart-gift-selector__item');
  if (items.length < 2) return list.clientWidth || 1;
  return items[1].offsetLeft - items[0].offsetLeft || list.clientWidth || 1;
}

/** @param {HTMLElement} list */
function giftIndex(list) {
  return Math.round(list.scrollLeft / giftStep(list));
}

/** @param {HTMLElement} list @param {number} index */
function goToGift(list, index) {
  const count = list.querySelectorAll('.cart-gift-selector__item').length;
  if (!count) return;
  const target = ((index % count) + count) % count;
  list.scrollTo({ left: target * giftStep(list), behavior: 'smooth' });
}

/** @param {HTMLElement} list */
function syncGiftDots(list) {
  const selector = list.closest('cart-gift-selector-component');
  if (!selector) return;
  const active = giftIndex(list);
  selector.querySelectorAll('.cart-gift-selector__dot').forEach((dot, index) => {
    if (index === active) dot.setAttribute('aria-current', 'true');
    else dot.removeAttribute('aria-current');
  });
}

/** @param {MouseEvent} event */
function onGiftNavClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const control = target.closest('.cart-gift-selector__nav-button, .cart-gift-selector__dot');
  if (!control) return;
  const selector = control.closest('cart-gift-selector-component');
  const list = selector?.querySelector('.cart-gift-selector__list');
  if (!(list instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();

  if (control.classList.contains('cart-gift-selector__dot')) {
    goToGift(list, Number(control.getAttribute('data-index') ?? 0));
  } else {
    const direction = control.classList.contains('cart-gift-selector__nav-button--prev') ? -1 : 1;
    goToGift(list, giftIndex(list) + direction);
  }
}

/** @param {Event} event */
function onGiftListScroll(event) {
  const list = event.target;
  if (list instanceof HTMLElement && list.classList.contains('cart-gift-selector__list')) syncGiftDots(list);
}

if (!window.__cartGiftSelectBound) {
  window.__cartGiftSelectBound = true;
  document.addEventListener('change', onChange);
  document.addEventListener('click', onGiftNavClick, true);
  // scroll doesn't bubble; capture catches the list's own scrolling.
  document.addEventListener('scroll', onGiftListScroll, { capture: true, passive: true });
}
